import { Component } from '@angular/core';
import * as XLSX from 'xlsx';
import * as alertifyjs from 'alertifyjs';
import Swal from 'sweetalert2';
import { DirectDebitService } from '../../services/direct-debit/direct-debit.service';

/**
 * Registro de cobros de domiciliación desde archivo (remediación).
 * Vista incremental: subes el Excel + n° de lote, ves cada cobro clasificado y
 * registras de forma controlada (uno, los siguientes N, o todos).
 * El registro real lo hace el backend reusando la lógica de la conciliación;
 * es idempotente (nunca registra un pago repetido).
 */
@Component({
  selector: 'app-registro-cobros-archivo',
  templateUrl: './registro-cobros-archivo.component.html',
  styleUrls: ['./registro-cobros-archivo.component.css']
})
export class RegistroCobrosArchivoComponent {

  lote = '';
  fileName = '';
  rows: any[] = [];               // filas crudas del Excel
  loteInfo: any = null;           // contexto del lote (del backend)
  items: any[] = [];              // clasificados (del preview)
  resumen: any = null;

  consulting = false;             // consultando lote
  loteOk = false;                 // lote validado
  loading = false;                // analizando (preview)
  registering = false;            // registrando
  stopRequested = false;
  progress = { done: 0, total: 0 };
  searchText = '';
  errorMsg = '';

  readonly BATCH = 20;

  constructor(private dd: DirectDebitService) { }

  // ---------- paso 1: consultar lote ----------
  async consultarLote(): Promise<void> {
    if (!this.lote?.trim()) { alertifyjs.warning('Indica el número de lote.'); return; }
    this.consulting = true; this.errorMsg = '';
    // al reconsultar, limpio todo lo posterior
    this.loteOk = false; this.loteInfo = null; this.items = []; this.resumen = null;
    this.fileName = ''; this.rows = [];
    try {
      const resp = await this.dd.cobrosArchivoLote(this.lote.trim());
      if (!resp?.ok) { this.errorMsg = resp?.msg || 'No se pudo consultar el lote.'; alertifyjs.error(this.errorMsg); this.consulting = false; return; }
      this.loteInfo = resp.lote;
      this.loteOk = true;
    } catch (e: any) {
      this.errorMsg = e?.error?.msg || e?.message || 'Error al consultar el lote.';
      alertifyjs.error(this.errorMsg);
    }
    this.consulting = false;
  }

  onLoteChange(): void {
    // si cambian el lote, hay que volver a consultar
    this.loteOk = false; this.loteInfo = null; this.items = []; this.resumen = null; this.fileName = ''; this.rows = [];
  }

  // ---------- carga del archivo ----------
  onFileSelected(event: any): void {
    const file = event?.target?.files?.[0];
    if (file) this.parseExcel(file);
    if (event?.target) event.target.value = '';
  }

  async parseExcel(file: File): Promise<void> {
    this.fileName = file.name;
    this.items = []; this.resumen = null; this.errorMsg = '';
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(new Uint8Array(buf), { type: 'array' });
      const ws = wb.Sheets['Cobros efectivos'] || wb.Sheets[wb.SheetNames[0]];
      const aoa: any[] = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
      const rows: any[] = [];
      for (let i = 1; i < aoa.length; i++) {                 // saltar encabezado
        const r = aoa[i] || [];
        const reference = String(r[2] ?? '').trim();          // Referencia
        const cliente = String(r[0] ?? '').trim();
        if (!reference || /total/i.test(cliente)) continue;
        const montoBs = typeof r[3] === 'number' ? r[3] : parseFloat(String(r[3] ?? '').replace(/[^0-9.\-]/g, '')) || 0;
        rows.push({ cliente, cedula: String(r[1] ?? '').trim(), reference, montoBs });
      }
      this.rows = rows;
      alertifyjs.message(`Archivo leído: ${rows.length} cobros.`);
      await this.analizar();   // el lote ya está validado → analizar de una vez
    } catch (e) {
      console.error(e);
      alertifyjs.error('No se pudo leer el Excel.');
    }
  }

  // ---------- vista previa (solo lectura) ----------
  async analizar(): Promise<void> {
    if (!this.loteOk) { alertifyjs.warning('Consulta primero el lote.'); return; }
    if (!this.rows.length) { alertifyjs.warning('Sube el archivo de cobros.'); return; }
    this.loading = true; this.items = []; this.resumen = null; this.errorMsg = '';
    try {
      const resp = await this.dd.cobrosArchivoPreview(this.lote.trim(), this.rows);
      if (!resp?.ok) {
        this.errorMsg = resp?.msg || 'No se pudo generar la vista previa.';
        alertifyjs.error(this.errorMsg);
        this.loading = false; return;
      }
      this.items = resp.items || [];
      this.resumen = resp.resumen || null;
    } catch (e: any) {
      this.errorMsg = e?.error?.msg || e?.message || 'Error al analizar.';
      console.error(e); alertifyjs.error(this.errorMsg);
    }
    this.loading = false;
  }

  get pendientes(): any[] { return this.items.filter(i => i.estado === 'A_REGISTRAR'); }

  get filteredItems(): any[] {
    const t = (this.searchText || '').trim().toLowerCase();
    if (!t) return this.items;
    return this.items.filter(i =>
      (i.cliente || '').toLowerCase().includes(t) ||
      (i.cedula || '').toLowerCase().includes(t) ||
      (i.reference || '').toLowerCase().includes(t) ||
      String(i.credito || '').toLowerCase().includes(t)
    );
  }

  // ---------- registro ----------
  async registrarUno(item: any): Promise<void> {
    const r = await Swal.fire({
      icon: 'warning',
      title: '¿Registrar este cobro?',
      html: `Cliente <b>${item.cliente || '-'}</b><br>Crédito <b>${item.credito}</b> · Cuota <b>${item.cuota}</b><br>Monto <b>${item.montoUsd} USD</b> (${item.montoBs} Bs)<br><br>Queda registrado a tu nombre y <b>no se puede deshacer</b>.`,
      showCancelButton: true, confirmButtonText: 'Sí, registrar', cancelButtonText: 'Cancelar',
      confirmButtonColor: '#2563eb', reverseButtons: true, focusCancel: true
    });
    if (!r.isConfirmed) return;
    await this.registrarMany([item], false);
  }

  async registrarSiguientes(n: number): Promise<void> {
    const targets = this.pendientes.slice(0, n);
    if (!targets.length) { alertifyjs.message('No hay cobros pendientes.'); return; }
    await this.confirmAndRun(targets, `Registrar los siguientes ${targets.length} cobros`);
  }

  async registrarTodos(): Promise<void> {
    const targets = this.pendientes;
    if (!targets.length) { alertifyjs.message('No hay cobros pendientes.'); return; }
    await this.confirmAndRun(targets, `Registrar TODOS los ${targets.length} cobros pendientes`);
  }

  private async confirmAndRun(targets: any[], title: string): Promise<void> {
    const totalUsd = targets.reduce((a, i) => a + (i.montoUsd || 0), 0);
    const r = await Swal.fire({
      icon: 'warning', title: `${title}?`,
      html: `Se registrarán <b>${targets.length}</b> pagos por <b>${totalUsd.toFixed(2)} USD</b>.<br>Queda a tu nombre y <b>no se puede deshacer</b>.`,
      showCancelButton: true, confirmButtonText: 'Sí, registrar', cancelButtonText: 'Cancelar',
      confirmButtonColor: '#2563eb', reverseButtons: true, focusCancel: true
    });
    if (!r.isConfirmed) return;
    await this.registrarMany(targets, true);
  }

  private async registrarMany(targets: any[], batch: boolean): Promise<void> {
    this.registering = true; this.stopRequested = false;
    this.progress = { done: 0, total: targets.length };
    const byRef: any = {};
    this.items.forEach(i => byRef[i.reference] = i);

    try {
      for (let i = 0; i < targets.length; i += this.BATCH) {
        if (this.stopRequested) break;
        const chunk = targets.slice(i, i + this.BATCH).map(t => ({ reference: t.reference, montoBs: t.montoBs }));
        const resp = await this.dd.cobrosArchivoRegistrar(this.lote.trim(), chunk);
        if (resp?.ok && Array.isArray(resp.results)) {
          for (const res of resp.results) {
            const it = byRef[res.reference] || byRef[String(res.reference).replace(/^0+/, '')];
            if (it) { it.estado = res.estado; it.motivo = res.motivo; it.paymentCode = res.paymentCode; }
          }
        } else {
          alertifyjs.error(resp?.msg || 'Error registrando un bloque.');
        }
        this.progress.done = Math.min(targets.length, i + this.BATCH);
      }
    } catch (e) {
      console.error(e); alertifyjs.error('Error durante el registro.');
    }

    this.recomputeResumen();
    this.registering = false;
    const ok = targets.filter(t => t.estado === 'APLICADO').length;
    alertifyjs.success(`Listo: ${ok} aplicado(s)${this.stopRequested ? ' (detenido)' : ''}.`);
  }

  detener(): void { this.stopRequested = true; }

  private recomputeResumen(): void {
    if (!this.resumen) return;
    this.resumen.aRegistrar = this.items.filter(i => i.estado === 'A_REGISTRAR').length;
    this.resumen.aplicados = this.items.filter(i => i.estado === 'APLICADO').length;
    this.resumen.saltados = this.items.filter(i => i.estado === 'SALTADO').length;
    this.resumen.anomalias = this.items.filter(i => i.estado === 'ANOMALIA').length;
    this.resumen.fallidos = this.items.filter(i => i.estado === 'FALLIDO').length;
  }

  // ---------- descarga ----------
  descargarExcel(): void {
    if (!this.items.length) return;
    const headers = ['Estado', 'Motivo', 'Cliente', 'Cédula', 'Referencia', 'Crédito', 'Cuota', 'Monto Bs', 'Monto USD', 'Cód. pago'];
    const data = this.items.map(i => [i.estado, i.motivo || '', i.cliente || '', i.cedula || '', i.reference, i.credito || '', i.cuota || '', i.montoBs, i.montoUsd, i.paymentCode || '']);
    const ws = XLSX.utils.aoa_to_sheet([headers, ...data]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Registro cobros');
    const stamp = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(wb, `registro-cobros-${this.lote || 'lote'}-${stamp}.xlsx`);
  }

  estadoClass(estado: string): string {
    switch (estado) {
      case 'A_REGISTRAR': return 'bg-blue-100 text-blue-700';
      case 'APLICADO': return 'bg-green-100 text-green-700';
      case 'SALTADO': return 'bg-gray-100 text-gray-600';
      case 'ANOMALIA': return 'bg-amber-100 text-amber-700';
      case 'FALLIDO': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-600';
    }
  }
  estadoLabel(estado: string): string {
    return { A_REGISTRAR: 'A registrar', APLICADO: 'Aplicado', SALTADO: 'Saltado', ANOMALIA: 'Anomalía', FALLIDO: 'Fallido' }[estado] || estado;
  }
  trackByRef = (_: number, i: any) => i.reference;
}
